import { CloseFilledIcon } from '@/components/Icons';
import useFolderNames from '@/hooks/useFolderNames';
import { getAudioDetail } from '@/service/audioList';
import { useAudioDetailStore } from '@/state/audio-detail-store';
import { useAudioListStore } from '@/state/audio-list-store';
import { cn } from '@/utils';
import { useRequest } from 'ahooks';
import { default as React, useEffect } from 'react';
import { HistoryInfo } from './HistoryInfo';
import { InputInfo } from './InputInfo';
import { Properties } from './Properties';
import { StyleFilterConfigs } from './StyleFilterConfigs';
import { SongDetailHeader } from './SongDetailHeader';
import { Sorting } from './Sorting';
import { TrackControl } from './TrackControl';
import { TrackName } from './TrackName';
import { StyleFilter } from './StyleFilter';
import { useSessionStore } from '@/hooks/useSessionStore';

export const SongDetail: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { selectedInfo, setSelectedInfo } = useAudioListStore();
  const { audioDetail: selectedSongDetail, setAudioDetail } = useAudioDetailStore();

  const sessionStore = useSessionStore();
  const { session } = sessionStore;
  const hasViewSFConfigPermission = session?.user?.permissions?.some(({ code }) => code === 'VIEW_SF_CONFIGS');

  const { run } = useRequest(
    (params: { id: string }) => {
      return getAudioDetail({ id: params.id });
    },
    {
      onSuccess(data) {
        setAudioDetail(data?.data);
      },
      manual: true,
    },
  );
  useEffect(() => {
    if (selectedInfo?.id) {
      setAudioDetail(null);
      run({ id: selectedInfo?.id });
    }
  }, [selectedInfo?.id]);

  const isLinkToHistory = selectedSongDetail?.id !== selectedInfo?.id;

  const folderName = useFolderNames(selectedSongDetail?.folder_id ?? 0);
  if (!selectedInfo || !selectedSongDetail) {
    return null;
  }
  return (
    <div className=" h-full  text-black bg-jen-background-primary  overflow-y-scroll">
      <div className="flex flex-col justify-between h-full">
        <div>
          <SongDetailHeader
            isLinkToHistory={isLinkToHistory}
            onClose={onClose}
            selectedName={selectedInfo.name}
            setSelectedInfo={setSelectedInfo}
            closeHistory={() => {
              run({ id: selectedInfo?.id });
            }}
            audioInfo={selectedSongDetail}
          />
          <TrackName createAt={selectedSongDetail.created_at} name={selectedSongDetail.name} id={selectedSongDetail.id} />
          <TrackControl selectedSongDetail={selectedSongDetail} />
          <InputInfo fetchSongDetail={run} selectedSongDetail={selectedSongDetail} />
          <Sorting folderName={folderName} id={selectedSongDetail.id} />
          <StyleFilter styleFilterObj={selectedSongDetail.stylefilterDetail} metadata={selectedSongDetail.metadata} />
        </div>
        <div>
          <HistoryInfo
            fetchSongDetail={run}
            history={selectedSongDetail?.history}
            currentMetadata={{ ...selectedSongDetail?.metadata, created_at: selectedSongDetail?.created_at }}
          />
          <Properties selectedSongDetail={selectedSongDetail} />
          {selectedSongDetail.stylefilter && hasViewSFConfigPermission && <StyleFilterConfigs selectedSongDetail={selectedSongDetail} />}
        </div>
      </div>
    </div>
  );
};

interface ButtonProps {
  text?: string;
  className?: string;
  icon?: React.ReactNode;
  onClose?: () => void;
  onClick?: () => void;
}
export const Button: React.FC<ButtonProps> = ({ text, className, icon, onClose, onClick }) => {
  return (
    <div
      className={cn(
        'bg-jen-background-tertiary text-jen-interactive-secondary flex items-center justify-center py-jen-spacing-8 px-jen-spacing-8 pr-jen-spacing-12 border border-jen-border rounded-lg cursor-pointer  ',
        className,
      )}
      onClick={onClick}
    >
      {icon}
      <div className="text-[12px] font-pressura-mono">{text}</div>
      {Boolean(onClose) && (
        <CloseFilledIcon
          className="w-[16px] h-[16px] border-l border-jen-border ml-[12px] pl-[12px] box-content"
          fillColor="currentColor"
          onClick={(e) => {
            onClose?.();
          }}
        />
      )}
    </div>
  );
};
